import React, { useEffect, useState } from "react";
import {
  Box,
  Icon,
  KeyValuePairs,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsProduct } from "../../lusca-search-api/generated-src";

// These UTC start times are the existing producer schedules, not completion estimates.
const scheduledHour: Record<OmsProduct, number> = {
  channels: 15,
  addon: 0,
  tvod: 11,
};
const dayMs = 24 * 60 * 60 * 1000;
const staleAfterMs = 36 * 60 * 60 * 1000;

export function reportTimestamp(value: string | number): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Refresh time unavailable";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

export function scheduledRefresh(product: OmsProduct, now: number) {
  const last = new Date(now);
  last.setUTCHours(scheduledHour[product], 0, 0, 0);
  if (last.getTime() > now) last.setUTCDate(last.getUTCDate() - 1);
  return { last: last.getTime(), next: last.getTime() + dayMs };
}

export default function OMSReportSchedule({
  product,
  sourceRefreshedAt,
}: {
  product: OmsProduct;
  sourceRefreshedAt: string;
}) {
  const [now, setNow] = useState(Date.now);
  const refreshed = Date.parse(sourceRefreshedAt);
  const schedule = scheduledRefresh(product, now);
  const valid = Number.isFinite(refreshed);
  const stale = valid && now - refreshed >= staleAfterMs;
  const awaiting = valid && refreshed < schedule.last;
  useEffect(() => {
    let timer: number | undefined;
    function check() {
      const current = Date.now();
      setNow(current);
      const { next } = scheduledRefresh(product, current);
      const staleAt = refreshed + staleAfterMs;
      const boundary = staleAt > current ? Math.min(next, staleAt) : next;
      timer = window.setTimeout(check, Math.max(1, boundary - current));
    }
    function resume() {
      window.clearTimeout(timer);
      check();
    }
    check();
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [product, refreshed]);
  let notice: string | undefined;
  if (stale) {
    notice = "Data is at least 36 hours old. You can still export this report.";
  } else if (awaiting) {
    notice =
      "No updated report is available yet. You can still export this report.";
  }
  return (
    <div>
      <div className="oms-report-card__summary">
        {valid ? (
          <Box>
            <Box display="inline" color="text-body-secondary">
              Data refreshed
            </Box>{" "}
            <strong>
              <time dateTime={sourceRefreshedAt}>
                {reportTimestamp(sourceRefreshedAt)}
              </time>
            </strong>
          </Box>
        ) : (
          <div role="status">
            <Box color="text-body-secondary">
              Report refresh time unavailable.
            </Box>
          </div>
        )}
        <Popover
          header="Refresh schedule"
          position="bottom"
          size="large"
          triggerType="text"
          dismissAriaLabel="Close refresh schedule"
          content={
            <SpaceBetween size="xs">
              <KeyValuePairs
                items={[
                  {
                    label: "Most recent scheduled start",
                    value: (
                      <time dateTime={new Date(schedule.last).toISOString()}>
                        {reportTimestamp(schedule.last)}
                      </time>
                    ),
                  },
                  {
                    label: "Next scheduled start",
                    value: (
                      <time dateTime={new Date(schedule.next).toISOString()}>
                        {reportTimestamp(schedule.next)}
                      </time>
                    ),
                  },
                ]}
              />
              <Box color="text-body-secondary" fontSize="body-s">
                These are scheduled start times. Updated data is available after
                processing finishes.
              </Box>
            </SpaceBetween>
          }
        >
          <span className="oms-report-download__icon-label">
            <span aria-hidden="true">
              <Icon name="calendar" />
            </span>
            <span>Refresh schedule</span>
          </span>
        </Popover>
      </div>
      {valid && (
        <div role="status" aria-atomic="true">
          {notice && (
            <div className="oms-report-card__notice">
              <span aria-hidden="true">
                <Icon
                  name={stale ? "status-warning" : "status-info"}
                  variant={stale ? "warning" : "info"}
                />
              </span>
              <Box color="text-body-secondary" fontSize="body-s">
                {notice}
              </Box>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
